import { Component, Host, h, Prop, Event, EventEmitter, Method, Element } from "@stencil/core";

import { StzhOverlayOpenEvent, StzhOverlayCloseEvent } from "../../index";

import { createBaseFocusTrapOptions } from "../../utils/overlay-utils";

import { createFocusTrap, FocusTrap } from "focus-trap";

const CLASS_BODY_OPEN = "stzh-overlay-open";

/**
 * @slot - Slot for any content
 */
@Component({
  tag: "stzh-overlay",
  styleUrl: "stzh-overlay.scss",
  scoped: true,
})
export class StzhOverlay {
  /** Whether overlay is open */
  @Prop({ mutable: true }) open: boolean = false;

  /** Stay in original position (true) or move to body (false) */
  @Prop() stay: boolean = true;

  /** Accessible label for dialog */
  @Prop({ attribute: "a11y-label" }) a11yLabel: string = "";

  /** Dialog open event */
  @Event() stzhOpen: EventEmitter<StzhOverlayOpenEvent>;

  /** Dialog close event */
  @Event() stzhClose: EventEmitter<StzhOverlayCloseEvent>;

  @Element() element: HTMLStzhOverlayElement;

  @Method()
  async show() {
    this.open = true;
    this.stzhOpen.emit({
      component: "stzh-overlay",
    });
  }

  @Method()
  async hide() {
    this.open = false;
    this.stzhClose.emit({
      component: "stzh-overlay",
    });
  }

  private trap: FocusTrap;
  private containerElement: HTMLElement;
  private dialogElement: HTMLDivElement;

  private getSiblings() {
    if (!this.containerElement) {
      return [];
    }

    return Array.from(this.containerElement.children).filter(child => child !== this.element);
  }

  private disableSiblings() {
    this.getSiblings().forEach(sibling => {
      sibling.setAttribute("aria-hidden", "true");
    });
  }

  private enableSiblings() {
    this.getSiblings().forEach(sibling => {
      sibling.removeAttribute("aria-hidden");
    });
  }

  private dialogShown() {
    document.body.classList.add(CLASS_BODY_OPEN);
    this.disableSiblings();

    if (this.trap) {
      this.trap.activate();
    }
  }

  private dialogHidden() {
    document.body.classList.remove(CLASS_BODY_OPEN);
    this.enableSiblings();

    if (this.trap) {
      this.trap.deactivate();
    }
  }

  connectedCallback() {
    if (!this.stay && this.element.parentElement !== document.body) {
      document.body.appendChild(this.element);
    }

    this.containerElement = this.element.parentElement;
  }

  componentDidRender() {
    this.open ? this.dialogShown() : this.dialogHidden();
  }

  componentDidUpdate() {
    this.trap.updateContainerElements(this.element);
  }

  componentDidLoad() {
    this.trap = createFocusTrap(this.element, {
      fallbackFocus: this.dialogElement,
      ...createBaseFocusTrapOptions(),
    });

    if (this.open) {
      this.dialogShown();
    }
  }

  disconnectedCallback() {
    this.dialogHidden();
  }

  render() {
    const classes = {
      "stzh-overlay": true,
      "stzh-overlay--is-open": this.open,
    };

    return (
      <Host>
        <div class={classes}>
          <div class="stzh-overlay__backdrop"></div>
          <div class="stzh-overlay__content">
            <div
              class="stzh-overlay__content-inner"
              ref={el => (this.dialogElement = el as HTMLDivElement)}
              tabindex="-1"
              role="dialog"
              aria-label={this.a11yLabel || null}
              aria-hidden={this.open ? "false" : "true"}
            >
              <slot></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
